'use client';

import CustomButton from '@/components/custom-button';
import InputPassword from '@/components/custom-input/input-password';
import CustomToast from '@/components/custom-toast';
import { template_warning } from '@/components/custom-toast/sonner-with-template';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { pageDirectories } from '@/constant/route';
import signInCredentials from '@/keys/signInCredentials';
import { Link, useRouter } from '@/navigation';
import { LoginInput } from '@/server/api/v1/auth/schema';
import { AuthErrorCode } from '@/server/config/auth.error';
import { LoginMethod, LoginTarget } from '@/types/enum';
import { DoorOpen, Lock, LogIn, UserRound } from 'lucide-react';
import { getSession, signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import React, { useCallback, useState } from 'react';
import { z } from 'zod';
import { ChangePasswordForm } from '../change-password-first-time-form';
import { Render3rdPartyLogin } from '../sign-in-form/3rd-party-login';
import { RenderAlertLogin } from '../sign-in-form/alert-login';
import { ObjectSignIn } from '../sign-in-form/object-signin';

const { sonner } = CustomToast;

type LoginError = { title: string; desc?: string };

export default function SsoSigninForm(props: {
    loginMethod: Record<LoginMethod, boolean>;
    isOGStudent?: boolean;
}) {
    const transDef = useTranslations('System');
    const transPage = useTranslations('SignIn');
    const transBrandInfo = useTranslations('BrandInfo');

    const [loginTarget, setLoginTarget] = useState<LoginTarget>(() =>
        props.isOGStudent ? LoginTarget.UIS_Student : LoginTarget.Credential
    );
    const [actNeeded, setActNeeded] = useState<{
        type: 'change-pwd';
        token: string;
        expireIn: number;
    }>();
    const [loginInfo, setLoginInfo] = useState<{
        username: string;
        password: string;
    }>();
    const [loginError, setLoginError] = useState<LoginError>();
    const [isLoading, setIsLoading] = useState(false);
    const [totalFail, setTotalFail] = useState(0);

    const router = useRouter();

    const handleAfterSignin = useCallback(async () => {
        const session = await getSession();
        if (session?.actionNeeded) {
            setActNeeded(session.actionNeeded);
        } else {
            window?.location?.reload() ?? router.refresh();
        }
    }, [router]);

    const onLoginWithAccount = useCallback(
        async (event: React.SyntheticEvent) => {
            event.preventDefault();
            setIsLoading(true);
            const inputs: any[] = event.target as any;
            try {
                setLoginError(undefined);

                const loginBody: z.infer<typeof LoginInput>['credentials'] = {
                    username: inputs[0].value,
                    password: inputs[1].value,
                    target: loginTarget,
                };

                if (!loginBody.username || !loginBody.password) {
                    setIsLoading(false);
                    sonner.warning(
                        template_warning({
                            title: transPage('PlsEnterFullInfomation'),
                        })
                    );
                    return;
                }

                const result = await signIn(signInCredentials.Credentials, {
                    ...loginBody,
                    redirect: false,
                });

                if (!(result?.error?.length && result.error.length > 0)) {
                    setLoginInfo(loginBody);
                    await handleAfterSignin();
                    return;
                }

                setIsLoading(false);

                if (result.code) {
                    let title = '';
                    switch (result.code) {
                        case AuthErrorCode.InvalidCredentials:
                            title = transPage('AccountInfoIncorrect');
                            break;
                        case AuthErrorCode.Forbidden:
                            title = transPage('AccountDoesNotHaveAccess');
                            break;
                        case AuthErrorCode.ErrorCivilServant:
                            title = transPage('ErrorCivilServant');
                            break;
                    }
                    setLoginError({
                        title,
                        desc:
                            totalFail > 3
                                ? transPage(
                                      'IfUThinkThisSystemErrorPlsContactAdmin'
                                  )
                                : '',
                    });
                } else {
                    setLoginError({
                        title: transDef('System_AnErrorHasOccurred'),
                        desc: transDef(
                            'System_PleaseTryAgainOrContactTheAdministrator'
                        ),
                    });
                }
            } catch {
                setLoginError({
                    title: transPage('AccountInfoIncorrect'),
                    desc:
                        totalFail > 3
                            ? transPage(
                                  'IfUThinkThisSystemErrorPlsContactAdmin'
                              )
                            : '',
                });
            }

            setTotalFail((x) => x + 1);
        },
        [handleAfterSignin, loginTarget, totalFail, transDef, transPage]
    );

    if (actNeeded?.type === 'change-pwd' && loginInfo) {
        return <ChangePasswordForm {...actNeeded} loginInfo={loginInfo} />;
    }

    return (
        <div className='space-y-4'>
            <h1 className='flex select-text items-center justify-center gap-2 text-balance text-base font-bold uppercase text-primary'>
                <div className='rounded-md bg-muted p-1'>
                    <DoorOpen className='w-8' />
                </div>
                {transBrandInfo('BrandInfo_Cust_AppName')}
            </h1>
            <h1 className='mb-3 text-[26px] font-bold tracking-wide text-primary lg:text-3xl'>
                {transPage('SignIn')}
            </h1>

            {props.isOGStudent && (
                <div className='pb-2'>
                    <ObjectSignIn
                        loginTarget={loginTarget}
                        setLoginTarget={setLoginTarget}
                        disabled={isLoading}
                    />
                </div>
            )}

            <form
                className='block space-y-4'
                onSubmit={onLoginWithAccount}
                autoComplete='off'
            >
                <div>
                    <Label
                        htmlFor='sso-username'
                        className='mb-1.5 flex items-center gap-1 text-sm font-medium'
                    >
                        <UserRound
                            size={14}
                            strokeWidth={2.5}
                            className='mb-0.5'
                        />
                        {transPage('UserName')}
                    </Label>
                    <Input
                        id='sso-username'
                        type='text'
                        autoCapitalize='none'
                        autoCorrect='off'
                        autoComplete='username'
                        disabled={isLoading}
                        placeholder={transPage('UserName')}
                        className='rounded-sm shadow-sm'
                    />
                </div>

                <div>
                    <div className='mb-1.5 flex items-center justify-between'>
                        <Label
                            htmlFor='sso-password'
                            className='flex items-center gap-1 text-sm font-medium'
                        >
                            <Lock
                                size={14}
                                strokeWidth={2.5}
                                className='mb-0.5'
                            />
                            {transPage('Password')}
                        </Label>
                        <Link
                            href={pageDirectories['Forgor']}
                            className='text-xs font-medium text-primary hover:underline dark:text-primary-foreground'
                            tabIndex={-1}
                        >
                            {transPage('ForgotPassword')}?
                        </Link>
                    </div>
                    <InputPassword
                        id='sso-password'
                        disabled={isLoading}
                        placeholder={transPage('Password')}
                        className='rounded-sm shadow-sm'
                    />
                </div>

                <RenderAlertLogin
                    isShow={!!loginError}
                    title={loginError?.title ?? ''}
                    desc={loginError?.desc}
                />

                <CustomButton.Root
                    type='submit'
                    
                    className='font-semibold! h-10 w-full'
                    isLoading={isLoading}
                    icon={<LogIn size={14} strokeWidth={2.5} />}
                >
                    {transPage('SignIn')}
                </CustomButton.Root>
            </form>

            <Render3rdPartyLogin
                isDisabledBtn={isLoading}
                loginTarget={loginTarget}
                {...props}
            />

            {props.isOGStudent && (
                <p className='mt-4 text-center text-sm text-muted-foreground'>
                    {transPage('NotAccount')}?{' '}
                    <Link
                        className='font-medium text-primary hover:underline'
                        href={pageDirectories['Signup']}
                    >
                        {transPage('SignUp')}.
                    </Link>
                </p>
            )}
        </div>
    );
}
